import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  IonContent,
  IonIcon,
  IonPage,
  IonDatetime,
  IonDatetimeButton,
  IonModal,
  useIonViewDidEnter,
  IonSpinner,
} from "@ionic/react";
import {
  BookTableBox,
  Button,
  LazyCoverImage,
  PageContainer,
  PageCover,
  RenderInfoBoxArgs,
  Skeleton,
} from "@greeter/matter";

import { addOutline, lockClosedOutline, lockOpenOutline } from "ionicons/icons";
import {
  TableId,
  LockedTable,
  FloorPlan,
  Area,
  Venue,
  ImageAsset,
  SpecialOpeningHours,
} from "@greeter/core";

import { assign, createMachine, setup, raise } from "xstate";
import { useActor, useMachine, useSelector } from "@xstate/react";
import { doNothing } from "@greeter/util";
import { Query } from "@greeter/query";
import { logger } from "@greeter/log";

import css from "./TableOverviewPage.module.scss";
import { first, partial } from "lodash";
import { getEnv } from "@greeter-venue/utility/ConfigHooks";
import {
  DateOnlySchema,
  isSameDayWithinMidnightPeriod,
  midnight,
  toDateString,
} from "@greeter/date";
import { any, exists } from "@greeter/array";
import {
  differenceInDays,
  endOfMonth,
  isSameDay,
  set,
  startOfMonth,
  subDays,
} from "date-fns";
import {
  TableOverviewApiRequirements,
  TableOverviewMachine,
} from "./TableOverviewMachine";
import { useFetchActorLoading } from "@greeter/xstate-util";

const log = logger("[TableOverviewPage]");

const Flex = ({ children }) => {
  return (
    <div
      style={{
        display: "flex",
        justifyItems: "center",
        alignItems: "center",
        gap: "1rem",
      }}
    >
      {children}
    </div>
  );
};

const ButtonBar = ({ children }) => {
  return (
    <div
      style={{
        display: "flex",
        padding: "1rem 0",
        gap: "1rem",
        width: "100%",
        height: "fit-content",
        justifyContent: "space-between",
      }}
    >
      {children}
    </div>
  );
};

const ActionButton = ({ children, onClick }) => {
  return (
    <Button
      style={{
        fontWeight: "bold",
        fontSize: "1rem",
        padding: "1rem 0",
        width: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        justifyItems: "center",
        background: "var(--gm-color-gradient-bg)",
      }}
      onClick={onClick}
    >
      {children}
    </Button>
  );
};

export type TableOverviewPageProps = React.PropsWithChildren & {
  onCreateBooking?: (tables: TableId[]) => Promise<void> | void;
  api: TableOverviewApiRequirements;
};

export function TableOverviewPage({
  onCreateBooking = doNothing,
  api,
}: TableOverviewPageProps) {
  const [state, send, m] = useActor(TableOverviewMachine, {
    input: { api },
  });

  const area = useSelector(m, (s) => s.context.area);
  const lockedTables = useSelector(m, (s) => s.context.lockedTables);
  const floorPlan = useSelector(
    state.context.api.fetchFloorPlanRef,
    (s) => s.context.data
  );
  const specialOpeningHours = useSelector(
    state.context.api.fetchSpecialOpeningHoursRef,
    (s) => s.context.data
  );
  const venue = useSelector(
    state.context.api.fetchVenueRef,
    (s) => s.context.data
  );

  const handleLockRender = useCallback(
    ({ table, bounds, el }: RenderInfoBoxArgs) => {
      if (
        lockedTables.some(
          (lt) => lt.tableNumber === table.number && lt.area === area
        )
      ) {
        return (
          <div
            style={{
              transform: `translate(calc(${
                bounds.x + bounds.width
              }px - 0.60rem), calc(${bounds.y}px - 0.25rem))`,
              position: "absolute",
            }}
          >
            <IonIcon
              icon={lockClosedOutline}
              style={{
                width: "1rem",
                height: "1rem",
                color: "white",
              }}
            />
          </div>
        );
      }
    },
    [lockedTables, area]
  );

  const bookableDates = useSelector(m, (s) => s.context.bookableDates);

  const isDateBookable = useCallback(
    (dateIsoString: string) => {
      return !!bookableDates.get(dateIsoString);
    },
    [bookableDates]
  );

  const unavailableTables = useSelector(m, (s) => [
    ...s.context.lockedTables,
    ...s.context.bookedTables,
  ]);

  const onTableClick = useCallback(
    (area: string, table: { number: number }) => {
      send({ type: "selectTable", data: { area, tableNumber: table.number } });
      send({ type: "validate" });
    },
    [send]
  );

  const selectedTables = useSelector(m, (s) => {
    return [...s.context.selectedLockedTables, ...s.context.selectedTables];
  });

  const onAreaChange = useCallback(
    (a: string) => {
      send({ type: "setArea", data: a });
    },
    [send]
  );

  const now = useMemo(() => new Date(), []);

  useIonViewDidEnter(() => {
    send({ type: "clearSelectedTables" });
  }, [send]);

  const fp: FloorPlan | undefined = { areas: floorPlan ?? [] };
  const env = getEnv();

  return (
    <IonPage>
      <IonContent>
        <div className={css.TableOverviewPage}>
          <PageCover>
            <LazyCoverImage
              src={
                venue
                  ? ImageAsset.findUriWithSizeOrDefault(
                      venue.coverAsset,
                      "16x9-w1024"
                    )
                  : ""
              }
              fade
            />
          </PageCover>
          <PageContainer>
            <h1 style={{ textAlign: "center" }}>Bordoversigt</h1>
            {!floorPlan ? (
              <Skeleton
                pulse
                style={{
                  aspectRatio: "1/1",
                  width: "100%",
                  boxSizing: "border-box",
                  margin: 0,
                }}
              ></Skeleton>
            ) : (
              floorPlan && (
                <BookTableBox
                  env={env}
                  bookedTables={unavailableTables}
                  selectedTables={selectedTables}
                  renderInfoBoxFooter={handleLockRender}
                  onTableClick={onTableClick}
                  onAreaChange={onAreaChange}
                  specialOpeningHours={specialOpeningHours}
                  floorPlan={fp}
                  groupSize={0}
                  display="number"
                  featureFlags={{ overlayOnOpeningHoursViolation: false }}
                />
              )
            )}
            <div>
              <IonDatetimeButton
                datetime="datetime"
                className={css.DateTimeButton}
              />
              <IonModal keepContentsMounted={true}>
                <IonDatetime
                  id="datetime"
                  firstDayOfWeek={1}
                  value={toDateString(state.context.date ?? new Date())}
                  onIonChange={(ev) => {
                    if (Array.isArray(ev.detail.value)) return;
                    send({
                      type: "setDate",
                      data: new Date(ev.detail.value ?? new Date()),
                    });
                  }}
                  min={toDateString(startOfMonth(now))}
                  isDateEnabled={isDateBookable}
                  presentation="date"
                  locale="da-DK"
                />
              </IonModal>
              {/* <DateInput date={date} onChange={setDate} /> */}
              {state.matches({ ui: "editLockedTables" }) ? (
                <ButtonBar>
                  <ActionButton
                    onClick={async () => {
                      send({ type: "unlockTables" });
                    }}
                  >
                    <Flex>
                      <IonIcon icon={lockOpenOutline} />
                      Frigør borde
                    </Flex>
                  </ActionButton>
                </ButtonBar>
              ) : (
                <ButtonBar>
                  <ActionButton
                    onClick={async () => {
                      send({ type: "lockTables" });
                    }}
                  >
                    <Flex>
                      <IonIcon icon={lockClosedOutline} />
                      Lås borde
                    </Flex>
                  </ActionButton>
                  <ActionButton onClick={() => onCreateBooking(selectedTables)}>
                    <Flex>
                      <IonIcon icon={addOutline} /> Opret booking
                    </Flex>
                  </ActionButton>
                </ButtonBar>
              )}
            </div>
          </PageContainer>
        </div>
      </IonContent>
    </IonPage>
  );
}
